import React, { useState, useEffect, useCallback } from 'react';
import {
  Zap,
  Clock,
  CheckCircle,
  XCircle,
  Activity,
  AlertTriangle
} from 'lucide-react';
import axios from '../../utils/axios';

const EvaluationProgressTracker = ({
  runId,
  onComplete,
  onError,
  showLogs = true,
  compact = false
}) => {
  const [progress, setProgress] = useState(null);
  const [logs, setLogs] = useState([]);
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState(null);

  // Fetch progress data
  const fetchProgress = useCallback(async () => {
    if (!runId || !isActive) return;

    // Check if user is authenticated
    const token = localStorage.getItem('auth_token');
    if (!token) {
      setError('Authentication required. Please log in.');
      setIsActive(false);
      return;
    }

    try {
      const response = await axios.get(`/evaluations/runs/${runId}/progress`);

      const data = response.data;
      setProgress(data);

      if (data.recent_logs) {
        setLogs(prev => {
          const newLogs = [...prev];
          data.recent_logs.forEach(log => {
            if (!newLogs.find(l => l.id === log.id)) {
              newLogs.push(log);
            }
          });
          return newLogs.slice(-20); // Keep last 20 logs
        });
      }

      // Handle completion
      if (data.status === 'completed') {
        setIsActive(false);
        if (onComplete) {
          onComplete(data);
        }
      } else if (data.status === 'failed') {
        setIsActive(false);
        if (onError) {
          onError(data);
        }
      }

      setError(null);
    } catch (err) {
      console.error('Error fetching progress:', err);
      setError(err.message);
      setIsActive(false);
    }
  }, [runId, isActive, onComplete, onError]);

  // Auto-refresh progress
  useEffect(() => {
    if (!runId) return;

    fetchProgress();

    if (isActive) {
      const interval = setInterval(fetchProgress, 1000);
      return () => clearInterval(interval);
    }
  }, [fetchProgress, isActive, runId]);

  // Get status icon
  const getStatusIcon = () => {
    if (!progress) return <Clock className="h-5 w-5 text-gray-500" />;

    switch (progress.status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'in_progress':
        return <Activity className="h-5 w-5 text-blue-500 animate-pulse" />;
      default:
        return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };

  // Get status color
  const getStatusColor = () => {
    if (!progress) return 'text-gray-600';

    switch (progress.status) {
      case 'completed':
        return 'text-green-600';
      case 'failed':
        return 'text-red-600';
      case 'in_progress':
        return 'text-blue-600';
      default:
        return 'text-gray-600';
    }
  };

  // Format duration
  const formatDuration = (ms) => {
    if (!ms) return '0s';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  // Render log entry
  const renderLogEntry = (log) => {
    const levelColors = {
      error: 'text-red-600 bg-red-50 border-red-200',
      warning: 'text-yellow-600 bg-yellow-50 border-yellow-200',
      info: 'text-blue-600 bg-blue-50 border-blue-200',
    };

    const levelIcons = {
      error: <XCircle className="h-4 w-4" />,
      warning: <AlertTriangle className="h-4 w-4" />,
      info: <Zap className="h-4 w-4" />,
    };

    return (
      <div
        key={log.id}
        className={`flex items-start space-x-2 p-2 rounded border ${levelColors[log.level] || levelColors.info}`}
      >
        <div className="flex-shrink-0 mt-0.5">
          {levelIcons[log.level] || levelIcons.info}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">{log.message}</div>
          <div className="text-xs opacity-75 mt-1">
            {new Date(log.timestamp).toLocaleTimeString()}
          </div>
        </div>
      </div>
    );
  };

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center space-x-2">
          <XCircle className="h-5 w-5 text-red-500" />
          <div>
            <h3 className="text-sm font-medium text-red-800">Progress Tracking Error</h3>
            <p className="text-sm text-red-600 mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!progress) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
        <span className="ml-2 text-gray-600">Loading progress...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Progress Header */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            {getStatusIcon()}
            <h3 className={`text-lg font-semibold capitalize ${getStatusColor()}`}>
              {progress.status.replace('_', ' ')}
            </h3>
          </div>
          <div className="text-sm text-gray-600">
            Run ID: {progress.run_id}
          </div>
        </div>

        {/* Current Step */}
        {progress.current_step && (
          <div className="mb-3">
            <div className="text-sm text-gray-600">Current Step</div>
            <div className="font-medium">{progress.current_message || progress.current_step}</div>
          </div>
        )}

        {/* Progress Bar */}
        {progress.progress_percentage > 0 && (
          <div className="mb-3">
            <div className="flex justify-between text-sm text-gray-600 mb-1">
              <span>Progress</span>
              <span>{progress.progress_percentage}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress.progress_percentage}%` }}
              ></div>
            </div>
          </div>
        )}

        {/* Stats */}
        {!compact && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-gray-500">Duration</div>
              <div className="font-medium">
                {progress.duration_human || formatDuration(progress.duration_ms)}
              </div>
            </div>
            <div>
              <div className="text-gray-500">Steps</div>
              <div className="font-medium">{progress.total_steps}</div>
            </div>
            <div>
              <div className="text-gray-500">Errors</div>
              <div className={`font-medium ${progress.error_count > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {progress.error_count}
              </div>
            </div>
            <div>
              <div className="text-gray-500">Last Updated</div>
              <div className="font-medium">
                {new Date(progress.last_updated).toLocaleTimeString()}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Recent Logs */}
      {showLogs && logs.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h4 className="text-md font-semibold">Recent Activity</h4>
          </div>
          <div className="p-4 space-y-2 max-h-64 overflow-y-auto">
            {logs.slice(-10).reverse().map(renderLogEntry)}
          </div>
        </div>
      )}
    </div>
  );
};

export default EvaluationProgressTracker;